import { BadgeId, RarityType } from "@/app/@types";
import { useAppStore } from "@/store/useAppStore";
import { badgeSystem } from "./badgeSystem";

export const gameRules = () => {
  const {
    predictions,
    userRank,
    userScore,
    badges,
    completedChallenges,
    crowdBeats,
    streak,
  } = useAppStore.getState();

  return {
    badges: {
      "first-challenge": () => {
        const current = predictions.length;
        const target = 1;
        return {
          earned: current >= target,
          progress: Math.min(100, (current / target) * 100),
        };
      },

      "system-1-survivor": () => {
        const qualifying = predictions.filter(
          (p) => p.question.includes("bat and ball") && p.points_earned > 50
        );
        const current = qualifying.length;
        const target = 1;
        return {
          earned: current >= target,
          progress: Math.min(100, (current / target) * 100),
        };
      },

      "crowd-beater": () => {
        const current = crowdBeats;
        const target = 5;
        return {
          earned: current >= target,
          progress: Math.min(100, (current / target) * 100),
        };
      },

      "bias-slayer": () => {
        const qualifying = predictions.filter(
          (p) => p.points_earned > p.points_earned * 0.8
        );
        const current = qualifying.length;
        const target = 10;
        return {
          earned: current >= target,
          progress: Math.min(100, (current / target) * 100),
        };
      },

      "streak-master": () => {
        const current = streak;
        const target = 10;
        return {
          earned: current >= target,
          progress: Math.min(100, (current / target) * 100),
        };
      },

      "perfect-calibration": () => {
        const estimates = predictions.filter((p) => p.prediction !== null);
        const goodEstimates = estimates.filter((p) => p.points_earned > 0.9);
        const currentEstimates = estimates.length;
        const currentGood = goodEstimates.length;
        const targetEstimates = 20;
        const targetGood = 18;

        const estimatesProgress = Math.min(
          100,
          (currentEstimates / targetEstimates) * 100
        );
        const qualityProgress =
          currentEstimates >= targetEstimates
            ? Math.min(100, (currentGood / targetGood) * 100)
            : 0;

        return {
          earned:
            currentEstimates >= targetEstimates && currentGood >= targetGood,
          progress: Math.min(100, Math.max(estimatesProgress, qualityProgress)),
        };
      },

      "thaler-scholar": () => {
        const qualifying = predictions.filter(
          (p) =>
            p.question.includes("endowment") || p.question.includes("nudge")
        );
        const current = qualifying.length;
        const target = 5;
        return {
          earned: current >= target,
          progress: Math.min(100, (current / target) * 100),
        };
      },

      "market-prophet": () => {
        const qualifying = predictions.filter(
          (p) => p.status === "resolved" && p.points_earned > 0.8
        );
        const current = qualifying.length;
        const target = 5;
        return {
          earned: current >= target,
          progress: Math.min(100, (current / target) * 100),
        };
      },

      "nobel-laureate": () => {
        const rankProgress =
          userRank !== null
            ? Math.min(100, Math.max(0, ((51 - userRank) / 50) * 100))
            : 0;
        const scoreProgress = Math.min(100, (userScore / 10000) * 100);
        const meetsRank = userRank !== null && userRank <= 50;
        const meetsScore = userScore >= 10000;

        return {
          earned: meetsRank && meetsScore,
          progress: Math.min(100, (rankProgress + scoreProgress) / 2),
        };
      },

      "grand-master": () => {
        const totalBadges = Object.keys(badgeSystem).length - 1;
        const rankProgress = userRank === 1 ? 100 : 0;
        const scoreProgress = Math.min(100, (userScore / 25000) * 100);
        const badgeProgress = Math.min(
          100,
          (badges.length / totalBadges) * 100
        );

        const meetsAllCriteria =
          userRank === 1 && userScore >= 25000 && badges.length === totalBadges;

        return {
          earned: meetsAllCriteria,
          progress: Math.min(
            100,
            (rankProgress + scoreProgress + badgeProgress) / 3
          ),
        };
      },

      "disciplined-trader": () => {
        const rookieIds = [
          "rookie-1",
          "rookie-2",
          "rookie-3",
          "rookie-4",
          "rookie-5",
        ];
        const completed = rookieIds.filter((id) =>
          completedChallenges.includes(id)
        );
        return {
          earned: completed.length === rookieIds.length,
          progress: Math.min(100, (completed.length / rookieIds.length) * 100),
        };
      },

      "market-historian": () => {
        const legendIds = [
          "legend-1",
          "legend-2",
          "legend-3",
          "legend-4",
          "legend-5",
        ];
        const completed = legendIds.filter((id) =>
          completedChallenges.includes(id)
        );
        return {
          earned: completed.length === legendIds.length,
          progress: Math.min(100, (completed.length / legendIds.length) * 100),
        };
      },

      "sharp-bettor": () => {
        const sportsIds = [
          "sports-1",
          "sports-2",
          "sports-3",
          "sports-4",
          "sports-5",
        ];
        const completed = sportsIds.filter((id) =>
          completedChallenges.includes(id)
        );
        return {
          earned: completed.length === sportsIds.length,
          progress: Math.min(100, (completed.length / sportsIds.length) * 100),
        };
      },

      "diamond-hands": () => {
        const cryptoIds = [
          "crypto-1",
          "crypto-2",
          "crypto-3",
          "crypto-4",
          "crypto-5",
        ];
        const completed = cryptoIds.filter((id) =>
          completedChallenges.includes(id)
        );
        return {
          earned: completed.length === cryptoIds.length,
          progress: Math.min(100, (completed.length / cryptoIds.length) * 100),
        };
      },

      "crisis-alpha": () => {
        const crashIds = [
          "crash-1",
          "crash-2",
          "crash-3",
          "crash-4",
          "crash-5",
        ];
        const completed = crashIds.filter((id) =>
          completedChallenges.includes(id)
        );
        return {
          earned: completed.length === crashIds.length,
          progress: Math.min(100, (completed.length / crashIds.length) * 100),
        };
      },

      "volatility-hunter": () => {
        const optionsIds = [
          "options-1",
          "options-2",
          "options-3",
          "options-4",
          "options-5",
        ];
        const completed = optionsIds.filter((id) =>
          completedChallenges.includes(id)
        );
        return {
          earned: completed.length === optionsIds.length,
          progress: Math.min(100, (completed.length / optionsIds.length) * 100),
        };
      },
    } as Record<BadgeId, () => { earned: boolean; progress: number }>,

    // Scoring system
    scoring: {
      basePoints: {
        "choice-correct": 100,
        "choice-wrong": 25,
        "estimate-perfect": 200,
        "estimate-good": 100,
        "estimate-poor": 25,
      },
      multipliers: {
        "beat-crowd": 1.5,
        "streak-3": 1.1,
        "streak-7": 1.25,
        "streak-15": 1.5,
        "first-try": 1.2,
      },
    },

    // NFT Badge Properties
    nftProperties: {
      Common: {
        supply: "Unlimited",
        transferable: true,
        stakeable: false,
        marketValue: "No guaranteed value - market determined",
      },
      Uncommon: {
        supply: "Limited to 10,000",
        transferable: true,
        stakeable: true,
        marketValue: "No guaranteed value - market determined",
      },
      Rare: {
        supply: "Limited to 1,000",
        transferable: true,
        stakeable: true,
        marketValue: "No guaranteed value - market determined",
      },
      Legendary: {
        supply: "Limited to 100",
        transferable: true,
        stakeable: true,
        marketValue: "No guaranteed value - market determined",
      },
      Mythic: {
        supply: "Limited to 10",
        transferable: true,
        stakeable: true,
        marketValue: "No guaranteed value - market determined",
      },
    } as Record<
      RarityType,
      {
        supply: string;
        transferable: boolean;
        stakeable: boolean;
        marketValue: string;
      }
    >,
  };
};
